import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthProvider';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, ShieldAlert, KeyRound, CheckCircle2, AlertTriangle } from 'lucide-react';
import { formatApiErrorDetail } from '../lib/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Full-screen gate shown when `user.password_reset_required === true`.
 * Forces the member to set a new permanent password before any other
 * authenticated UI is reachable. Submitting `/api/auth/change-password`
 * clears the flag on the backend and returns a fresh JWT bound to the
 * incremented token_version, so this tab stays signed in cleanly.
 */
export const ForcePasswordChangeGate = ({ children }) => {
  const { user, authHeader, updateToken, refresh, logout } = useAuth();

  const required = !!user && user.password_reset_required === true;
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!required) return children;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Your new password must be different from the temporary one.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: authHeader() },
      );
      if (data.access_token) updateToken(data.access_token);
      setSuccess(true);
      // Refresh /auth/me so user.password_reset_required flips to false and the gate releases.
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10"
      data-testid="force-password-change-gate"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Set a new password</h1>
            <p className="text-xs text-gray-500">Required before you can continue</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-3 mb-5">
          An administrator reset your password. For your security, please set a new permanent password to continue using DriveHR.
        </p>

        {success ? (
          <div
            className="p-3 rounded-lg border bg-green-50 border-green-200 text-green-700 text-sm flex items-start gap-2"
            data-testid="force-password-change-success"
          >
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Password updated. Loading your account…</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="force-password-change-form">
            {error && (
              <div
                className="p-3 rounded-lg border bg-red-50 border-red-200 text-red-700 text-sm flex items-start gap-2"
                data-testid="force-password-change-error"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div>
              <Label htmlFor="fpc-cur">Temporary password</Label>
              <Input
                id="fpc-cur"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoFocus
                data-testid="force-password-change-current"
              />
              <p className="text-xs text-gray-500 mt-1">The one your admin shared with you.</p>
            </div>
            <div>
              <Label htmlFor="fpc-new">New password</Label>
              <Input
                id="fpc-new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                data-testid="force-password-change-new"
              />
              <p className="text-xs text-gray-500 mt-1">At least 8 characters.</p>
            </div>
            <div>
              <Label htmlFor="fpc-conf">Confirm new password</Label>
              <Input
                id="fpc-conf"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                data-testid="force-password-change-confirm"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="force-password-change-submit"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</>
              ) : (
                <><KeyRound className="w-4 h-4 mr-2" />Set new password</>
              )}
            </Button>
          </form>
        )}

        <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
          <span>Signed in as <strong className="text-gray-700">{user?.email}</strong></span>
          <button
            type="button"
            onClick={logout}
            className="text-blue-600 hover:underline font-medium"
            data-testid="force-password-change-logout"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};
